import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {addDays, differenceInCalendarDays, isValid, parse} from 'date-fns';
import {fetchSearch} from 'src/services/api/api';
import {getLocaleWithRegionCode, getTextFromLocale} from 'src/services/i18n/locale';
import {MAX_FUTURE_DATE_DAYS, MAX_DATE_RANGE_DAYS} from 'src/lib/dates/constants';
import {
    CategorizedDropDown,
    CategorizedDropdownCategory,
    CategorizedDropdownOption
} from '../form/categorized-dropdown/CategorizedDropdown';
import ModalPortal from '../layout/modal/ModalPortal';
import {format} from 'src/lib/dates/format';
import {MapBooleanActions, useMapDataContext} from 'src/context/MapDataContext';
import {GpsIcon} from '../icons/GpsIcon';
import {PositionPermissionError} from '../errors/PositionPermissionError';
import {MapControl} from '../map/map-control/MapControl';
import './QuickStartModal.scss';

const quickStartSeenStorageKey = 'ivt-quick-start-seen';
const quickStartPositionOptionId = 'my-position';

const quickStartReasonRouteByCategoryId: Record<string, string> = {
    'my-bookings': '/hantera',
    'my-vaccination-history': '/vaccinationshistorik'
};

interface SelectedOption {
    id: string;
    label: string;
    categoryId: string;
}

const GEOLOCATION_PERMISSION_DENIED = 1;

export function QuickStartModal() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {
        state: {visitReasons, careGivers, clinics},
        dispatch: mapDataDispatch
    } = useMapDataContext();
    const [open, setOpen] = useState(false);
    const [selectedReasonOptions, setSelectedReasonOptions] = useState<Array<SelectedOption>>([]);
    const [selectedWhereOptions, setSelectedWhereOptions] = useState<Array<SelectedOption>>([]);
    const [selectedDateOptionId, setSelectedDateOptionId] = useState('asap');
    const [selectedTimeOptionId, setSelectedTimeOptionId] = useState('ALLDAY');
    const today = format(new Date(), 'yyyy-MM-dd');
    const maxFutureDate = format(addDays(new Date(), MAX_FUTURE_DATE_DAYS), 'yyyy-MM-dd');
    const [customFromDate, setCustomFromDate] = useState(() => today);
    const [customToDate, setCustomToDate] = useState(() =>
        format(addDays(new Date(), MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd')
    );
    const [whereQuery, setWhereQuery] = useState('');
    const [placeOptions, setPlaceOptions] = useState<Array<CategorizedDropdownOption>>([]);
    const [isWhereLoading, setIsWhereLoading] = useState(false);
    const [showIntervalLimitedNotice, setShowIntervalLimitedNotice] = useState(false);
    const [showPositionPermissionError, setShowPositionPermissionError] = useState(false);
    const [positionPermissionStatus, setPositionPermissionStatus] = useState<PermissionState>('prompt');
    const [isPositionPermissionCheckPending, setIsPositionPermissionCheckPending] = useState(false);
    const [submitAfterPositionPermissionCheck, setSubmitAfterPositionPermissionCheck] = useState(false);
    const whereSearchRequestRef = useRef(0);

    const parseDateString = useCallback((value: string) => {
        const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
        return isValid(parsedDate) ? parsedDate : null;
    }, []);

    const addMyPositionOption = useCallback(() => {
        const selectedOption: SelectedOption = {
            id: quickStartPositionOptionId,
            label: t('map_position_marker.text'),
            categoryId: 'position'
        };

        setSelectedWhereOptions((prevSelectedOptions) => {
            if (prevSelectedOptions.some(({id, categoryId}) => id === selectedOption.id && categoryId === 'position')) {
                return prevSelectedOptions;
            }

            return [...prevSelectedOptions, selectedOption];
        });
    }, [t]);

    const removeMyPositionOption = useCallback(() => {
        setSelectedWhereOptions((prevSelectedOptions) => {
            return prevSelectedOptions.filter(({id, categoryId}) => {
                return !(id === quickStartPositionOptionId && categoryId === 'position');
            });
        });
    }, []);

    const probeGeolocationPermission = useCallback(() => {
        return new Promise<'granted' | 'denied' | 'unknown'>((resolve) => {
            if (!('geolocation' in navigator)) {
                resolve('unknown');
                return;
            }

            const geolocation = (window.navigator as Navigator & {geolocation: Geolocation}).geolocation;

            geolocation.getCurrentPosition(
                () => {
                    resolve('granted');
                },
                (error: GeolocationPositionError) => {
                    if (error.code === GEOLOCATION_PERMISSION_DENIED) {
                        resolve('denied');
                        return;
                    }

                    resolve('unknown');
                },
                {
                    maximumAge: 0,
                    timeout: 6000
                }
            );
        });
    }, []);

    const categories = useMemo(() => {
        const locale = getLocaleWithRegionCode();
        const categoryLabels = {
            vaccination: t('quickstart.search_group_visit_reason.vaccination'),
            travel: t('quickstart.search_group_visit_reason.travel'),
            other: t('quickstart.search_group_visit_reason.other')
        };
        const categoryOrder = Object.keys(categoryLabels);

        const shortcuts = visitReasons
            .map((reason) => ({id: reason.id, label: getTextFromLocale(reason.name)}))
            .filter(({label}) => label.length > 0)
            .slice(0, 3);

        const grouped = visitReasons.reduce(
            (acc, reason) => {
                if (reason.category === 'travel' || reason.category === 'other') {
                    return acc;
                }

                const category = reason.category;
                const label = getTextFromLocale(reason.name);

                if (label.length < 1) {
                    return acc;
                }

                const option = {
                    id: reason.id,
                    label
                } satisfies CategorizedDropdownOption;

                if (acc[category]) {
                    acc[category].options.push(option);
                } else {
                    acc[category] = {
                        id: category,
                        label: categoryLabels[category],
                        options: [option]
                    };
                }

                return acc;
            },
            {} as Record<string, CategorizedDropdownCategory>
        );

        const sortedCategories = Object.values(grouped)
            .map((category) => {
                return {
                    ...category,
                    options: [...category.options].sort((a, b) => a.label.localeCompare(b.label, locale))
                } satisfies CategorizedDropdownCategory;
            })
            .sort((a, b) => {
                const aIndex = categoryOrder.indexOf(a.id);
                const bIndex = categoryOrder.indexOf(b.id);

                if (aIndex === -1 && bIndex === -1) {
                    return a.label.localeCompare(b.label, locale);
                }

                if (aIndex === -1) {
                    return 1;
                }

                if (bIndex === -1) {
                    return -1;
                }

                return aIndex - bIndex;
            });

        return [
            ...(shortcuts.length > 0
                ? [
                      {
                          id: 'shortcuts',
                          label: '',
                          options: shortcuts
                      } satisfies CategorizedDropdownCategory
                  ]
                : []),
            ...sortedCategories,
            {
                id: 'my-bookings',
                label: t('quickstart.reason_shortcuts.my_bookings'),
                options: []
            },
            {
                id: 'my-vaccination-history',
                label: t('quickstart.reason_shortcuts.my_vaccination_history'),
                options: []
            }
        ];
    }, [t, visitReasons]);

    const whereCategories = useMemo(() => {
        const mappedCareGivers = careGivers
            .filter(({label}) => label.length > 0)
            .map(({id, label}) => {
                return {
                    id: id.toString(),
                    label
                } satisfies CategorizedDropdownOption;
            });

        const mappedClinics = clinics
            .filter(({name}) => name.length > 0)
            .map(({id, name}) => {
                return {
                    id,
                    label: name
                } satisfies CategorizedDropdownOption;
            });

        const grouped: Array<CategorizedDropdownCategory> = [];

        if (placeOptions.length > 0) {
            grouped.push({
                id: 'places',
                label: t('search_group_place.text'),
                options: placeOptions
            });
        }

        if (mappedCareGivers.length > 0) {
            grouped.push({
                id: 'caregivers',
                label: t('search_group_caregiver.text'),
                options: mappedCareGivers
            });
        }

        if (mappedClinics.length > 0) {
            grouped.push({
                id: 'clinics',
                label: t('search_group_clinic.text'),
                options: mappedClinics
            });
        }

        return grouped;
    }, [careGivers, clinics, placeOptions, t]);

    const dateCategories = useMemo(() => {
        return [
            {
                id: 'date-options',
                label: t('quickstart.date.label'),
                options: [
                    {
                        id: 'asap',
                        label: t('quickstart.date_options.anytime')
                    },
                    {
                        id: 'within-week',
                        label: t('quickstart.date_options.within_week')
                    },
                    {
                        id: 'custom',
                        label: t('quickstart.date_options.custom')
                    }
                ]
            }
        ] satisfies Array<CategorizedDropdownCategory>;
    }, [t]);

    const timeCategories = useMemo(() => {
        return [
            {
                id: 'time-of-day',
                label: t('quickstart.time.label'),
                options: [
                    {
                        id: 'ALLDAY',
                        label: t('search_filters.time_of_day_options.allday')
                    },
                    {
                        id: 'MORNING',
                        label: t('search_filters.time_of_day_options.morning')
                    },
                    {
                        id: 'AFTERNOON',
                        label: t('search_filters.time_of_day_options.afternoon')
                    },
                    {
                        id: 'EVENING',
                        label: t('search_filters.time_of_day_options.evening')
                    }
                ]
            }
        ] satisfies Array<CategorizedDropdownCategory>;
    }, [t]);

    const selectedDateOptionLabel = useMemo(() => {
        const selectedOption = dateCategories[0]?.options.find(({id}) => id === selectedDateOptionId);
        return selectedOption?.label || t('quickstart.date.placeholder');
    }, [dateCategories, selectedDateOptionId, t]);

    const selectedTimeOptionLabel = useMemo(() => {
        const selectedOption = timeCategories[0]?.options.find(({id}) => id === selectedTimeOptionId);
        return selectedOption?.label || t('quickstart.time.placeholder');
    }, [selectedTimeOptionId, t, timeCategories]);

    useEffect(() => {
        const requestId = whereSearchRequestRef.current + 1;
        whereSearchRequestRef.current = requestId;
        const normalizedQuery = whereQuery.trim();

        if (normalizedQuery.length < 3) {
            setPlaceOptions([]);
            setIsWhereLoading(false);
            return;
        }

        setPlaceOptions([]);
        setIsWhereLoading(true);

        const abortController = new AbortController();
        const timeoutId = setTimeout(() => {
            fetchSearch(normalizedQuery, abortController.signal)
                .then((places) => {
                    if (whereSearchRequestRef.current !== requestId) {
                        return;
                    }

                    setPlaceOptions(
                        places.map((place) => {
                            return {
                                id: place.id,
                                label: place.name
                            } satisfies CategorizedDropdownOption;
                        })
                    );
                })
                .catch((error) => {
                    if ((error as Error).name === 'AbortError') {
                        return;
                    }

                    if (whereSearchRequestRef.current !== requestId) {
                        return;
                    }

                    setPlaceOptions([]);
                })
                .finally(() => {
                    if (whereSearchRequestRef.current === requestId) {
                        setIsWhereLoading(false);
                    }
                });
        }, 250);

        return () => {
            abortController.abort();
            clearTimeout(timeoutId);
        };
    }, [whereQuery]);

    const handleReasonSelect = (option: CategorizedDropdownOption, category: CategorizedDropdownCategory) => {
        const selectedOption: SelectedOption = {
            id: option.id,
            label: option.label,
            categoryId: category.id
        };

        setSelectedReasonOptions((prevSelectedOptions) => {
            if (prevSelectedOptions.some(({id}) => id === option.id)) {
                return prevSelectedOptions;
            }

            return [...prevSelectedOptions, selectedOption];
        });
    };

    const handleReasonCategorySelect = (category: CategorizedDropdownCategory) => {
        const route = quickStartReasonRouteByCategoryId[category.id];

        if (!route) {
            return;
        }

        navigate(route);
        closeQuickStart();
    };

    const handleWhereSelect = (option: CategorizedDropdownOption, category: CategorizedDropdownCategory) => {
        const selectedOption: SelectedOption = {
            id: option.id,
            label: option.label,
            categoryId: category.id
        };

        setSelectedWhereOptions((prevSelectedOptions) => {
            if (prevSelectedOptions.some(({id, categoryId}) => id === option.id && categoryId === category.id)) {
                return prevSelectedOptions;
            }

            return [...prevSelectedOptions, selectedOption];
        });
    };

    const handleMyPositionSelect = useCallback(async () => {
        if (isPositionPermissionCheckPending) {
            return;
        }

        setIsPositionPermissionCheckPending(true);

        try {
            let permissionState: PermissionState | 'unknown' = 'unknown';

            if ('permissions' in navigator) {
                try {
                    const permissions = await navigator.permissions.query({name: 'geolocation'});
                    permissionState = permissions.state;
                } catch {}
            }

            if (permissionState === 'denied') {
                setPositionPermissionStatus('denied');
                setShowPositionPermissionError(true);
                removeMyPositionOption();
                setSubmitAfterPositionPermissionCheck(false);
                return;
            }

            if (permissionState === 'granted') {
                setPositionPermissionStatus('granted');
                addMyPositionOption();
                return;
            }

            const probeResult = await probeGeolocationPermission();

            if (probeResult === 'denied') {
                setPositionPermissionStatus('denied');
                setShowPositionPermissionError(true);
                removeMyPositionOption();
                setSubmitAfterPositionPermissionCheck(false);
                return;
            }

            if (probeResult === 'granted') {
                setPositionPermissionStatus('granted');
            } else {
                setPositionPermissionStatus('prompt');
            }

            addMyPositionOption();
        } finally {
            setIsPositionPermissionCheckPending(false);
        }
    }, [addMyPositionOption, isPositionPermissionCheckPending, probeGeolocationPermission, removeMyPositionOption]);

    const removeSelectedReasonOption = (optionToRemove: SelectedOption) => {
        setSelectedReasonOptions((prevSelectedOptions) => {
            return prevSelectedOptions.filter(({id}) => id !== optionToRemove.id);
        });
    };

    const removeSelectedWhereOption = (optionToRemove: SelectedOption) => {
        setSelectedWhereOptions((prevSelectedOptions) => {
            return prevSelectedOptions.filter(({id, categoryId}) => {
                return id !== optionToRemove.id || categoryId !== optionToRemove.categoryId;
            });
        });
    };

    const handleCustomFromDateChange = useCallback(
        (date: string) => {
            if (!date) {
                return;
            }

            setCustomFromDate(date);
            setCustomToDate((prevDateTo) => {
                const parsedDateTo = parseDateString(prevDateTo);
                if (!parsedDateTo) {
                    setShowIntervalLimitedNotice(true);
                    return format(addDays(parse(date, 'yyyy-MM-dd', new Date()), MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');
                }

                const isInverted = differenceInCalendarDays(parsedDateTo, parse(date, 'yyyy-MM-dd', new Date())) < 0;
                const interval = Math.abs(
                    differenceInCalendarDays(parsedDateTo, parse(date, 'yyyy-MM-dd', new Date()))
                );
                if (isInverted || interval > MAX_DATE_RANGE_DAYS) {
                    setShowIntervalLimitedNotice(true);
                    return format(addDays(parse(date, 'yyyy-MM-dd', new Date()), MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');
                }

                return prevDateTo;
            });
        },
        [parseDateString]
    );

    const handleCustomToDateChange = useCallback(
        (date: string) => {
            if (!date) {
                return;
            }

            setCustomToDate(date);
            setCustomFromDate((prevDateFrom) => {
                const parsedDateFrom = parseDateString(prevDateFrom);
                if (!parsedDateFrom) {
                    setShowIntervalLimitedNotice(true);
                    return format(addDays(parse(date, 'yyyy-MM-dd', new Date()), -MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');
                }

                const isInverted = differenceInCalendarDays(parse(date, 'yyyy-MM-dd', new Date()), parsedDateFrom) < 0;
                const interval = Math.abs(
                    differenceInCalendarDays(parse(date, 'yyyy-MM-dd', new Date()), parsedDateFrom)
                );
                if (isInverted || interval > MAX_DATE_RANGE_DAYS) {
                    setShowIntervalLimitedNotice(true);
                    return format(addDays(parse(date, 'yyyy-MM-dd', new Date()), -MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');
                }

                return prevDateFrom;
            });
        },
        [parseDateString]
    );

    const handleDateSelect = (option: CategorizedDropdownOption) => {
        setSelectedDateOptionId(option.id);

        if (option.id !== 'custom') {
            setCustomFromDate(today);
            setCustomToDate(format(addDays(new Date(), MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd'));
        }
    };

    const handleTimeSelect = (option: CategorizedDropdownOption) => {
        setSelectedTimeOptionId(option.id);
    };

    useEffect(() => {
        if (!showIntervalLimitedNotice) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setShowIntervalLimitedNotice(false);
        }, 7000);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [showIntervalLimitedNotice]);

    useEffect(() => {
        try {
            const hasSeenQuickStart = sessionStorage.getItem(quickStartSeenStorageKey) === '1';
            const hasPresetSearch = new URLSearchParams(window.location.search).size > 0;

            if (!hasSeenQuickStart && !hasPresetSearch) {
                setOpen(true);
            }
        } catch {
            setOpen(true);
        }
    }, []);

    const closeQuickStart = useCallback(() => {
        setOpen(false);

        try {
            sessionStorage.setItem(quickStartSeenStorageKey, '1');
        } catch {}
    }, []);

    const applyQuickStartFilters = useCallback(() => {
        const hasPositionSelection = selectedWhereOptions.some(({categoryId, id}) => {
            return categoryId === 'position' && id === quickStartPositionOptionId;
        });

        if (hasPositionSelection && isPositionPermissionCheckPending) {
            setSubmitAfterPositionPermissionCheck(true);
            return;
        }

        const shouldLocateUser = selectedWhereOptions.some(({categoryId, id}) => {
            return categoryId === 'position' && id === quickStartPositionOptionId;
        });
        const reasonIds = selectedReasonOptions.map(({id}) => id);
        const caregiverIds = selectedWhereOptions
            .filter(({categoryId}) => categoryId === 'caregivers')
            .map(({id}) => id);
        const clinicIds = selectedWhereOptions.filter(({categoryId}) => categoryId === 'clinics').map(({id}) => id);
        const placeIds = selectedWhereOptions.filter(({categoryId}) => categoryId === 'places').map(({id}) => id);

        const params = new URLSearchParams();
        if (reasonIds.length > 0) {
            params.set('reason', reasonIds.join(','));
        }
        if (caregiverIds.length > 0) {
            params.set('caregiver', caregiverIds.join(','));
        }
        if (clinicIds.length > 0) {
            params.set('clinic', clinicIds.join(','));
        }
        if (placeIds.length > 0) {
            params.set('place', placeIds.join(','));
        }

        if (selectedTimeOptionId !== 'ALLDAY') {
            params.set('timeOfDay', selectedTimeOptionId);
        }

        if (selectedDateOptionId === 'within-week') {
            params.set('from', today);
            params.set('to', format(addDays(new Date(), 7), 'yyyy-MM-dd'));
        }

        if (selectedDateOptionId === 'custom') {
            params.set('from', customFromDate);
            params.set('to', customToDate);
        }

        const queryString = params.toString();
        navigate(queryString.length > 0 ? `/hitta?${queryString}` : '/hitta');

        if (shouldLocateUser) {
            mapDataDispatch({type: MapBooleanActions.SET_SHOULD_LOCATE_USER, value: true});
        }

        closeQuickStart();
    }, [
        closeQuickStart,
        customFromDate,
        customToDate,
        isPositionPermissionCheckPending,
        mapDataDispatch,
        navigate,
        selectedDateOptionId,
        selectedReasonOptions,
        selectedTimeOptionId,
        selectedWhereOptions,
        today
    ]);

    useEffect(() => {
        if (!submitAfterPositionPermissionCheck || isPositionPermissionCheckPending) {
            return;
        }

        setSubmitAfterPositionPermissionCheck(false);
        applyQuickStartFilters();
    }, [applyQuickStartFilters, isPositionPermissionCheckPending, submitAfterPositionPermissionCheck]);

    if (!open) {
        return null;
    }

    return (
        <>
            <ModalPortal
                size='sm'
                open={open}
                onCloseClick={closeQuickStart}
                onPrimaryAction={applyQuickStartFilters}
                primaryActionLabel={t('quickstart.primary_action')}
                headerTitle={<h1 className='quick-start-modal__header-title'>{t('quickstart.header_title')}</h1>}
                ariaLabel={t('quickstart.aria_label')}
                allowOverflowVisible
                showLanguagePicker
            >
                <div id='quick-start-modal' className='quick-start-modal'>
                    <div>
                        <label htmlFor='quickstart-search-reason'>{t('quickstart.reason.label')}</label>
                        <CategorizedDropDown
                            id='quickstart-search-reason'
                            placeholder={t('quickstart.reason.placeholder')}
                            categories={categories}
                            selectableCategoryIds={Object.keys(quickStartReasonRouteByCategoryId)}
                            flatOptionCategoryIds={['shortcuts']}
                            noResultsText={t('quickstart.no_results')}
                            onSelect={handleReasonSelect}
                            onCategorySelect={handleReasonCategorySelect}
                        />
                        {selectedReasonOptions.length > 0 && (
                            <ul className='quick-start-modal__selected-chips' aria-live='polite' aria-atomic='true'>
                                {selectedReasonOptions.map((selectedOption) => {
                                    return (
                                        <li key={selectedOption.id}>
                                            <button
                                                type='button'
                                                className='quick-start-modal__selected-chip'
                                                aria-label={`${t('quickstart.remove')}: ${selectedOption.label}`}
                                                onClick={() => {
                                                    removeSelectedReasonOption(selectedOption);
                                                }}
                                            >
                                                <span>{selectedOption.label}</span>
                                                <span
                                                    className='quick-start-modal__selected-chip-remove'
                                                    aria-hidden='true'
                                                >
                                                    x
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                    <div>
                        <label htmlFor='quickstart-search-where'>{t('quickstart.where.label')}</label>
                        <div className='quick-start-modal__where-row'>
                            <div className='quick-start-modal__where-dropdown'>
                                <CategorizedDropDown
                                    id='quickstart-search-where'
                                    placeholder={t('quickstart.where.placeholder')}
                                    categories={whereCategories}
                                    searchOnly
                                    openPanelOnFocus={false}
                                    isLoading={isWhereLoading}
                                    noResultsText={t('quickstart.no_results')}
                                    onSelect={handleWhereSelect}
                                    onQueryChange={setWhereQuery}
                                />
                            </div>
                            <div className='quick-start-modal__map-control'>
                                <MapControl
                                    color='primary'
                                    icon={<GpsIcon />}
                                    ariaLabel={t('find_position.text')}
                                    disabled={positionPermissionStatus === 'denied' || isPositionPermissionCheckPending}
                                    filled={selectedWhereOptions.some(({id, categoryId}) => {
                                        return id === quickStartPositionOptionId && categoryId === 'position';
                                    })}
                                    onClick={handleMyPositionSelect}
                                    onDisabledClick={() => {
                                        setShowPositionPermissionError(true);
                                    }}
                                />
                            </div>
                        </div>
                        {selectedWhereOptions.length > 0 && (
                            <ul className='quick-start-modal__selected-chips' aria-live='polite' aria-atomic='true'>
                                {selectedWhereOptions.map((selectedOption) => {
                                    return (
                                        <li key={`${selectedOption.categoryId}-${selectedOption.id}`}>
                                            <button
                                                type='button'
                                                className='quick-start-modal__selected-chip'
                                                aria-label={`${t('quickstart.remove')}: ${selectedOption.label}`}
                                                onClick={() => {
                                                    removeSelectedWhereOption(selectedOption);
                                                }}
                                            >
                                                <span>{selectedOption.label}</span>
                                                <span
                                                    className='quick-start-modal__selected-chip-remove'
                                                    aria-hidden='true'
                                                >
                                                    x
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                    <div>
                        <label htmlFor='quickstart-search-date'>{t('quickstart.visit.label')}</label>
                        <div className='quick-start-modal__date-time-row'>
                            <div className='quick-start-modal__date-time-item quick-start-modal__date-time-item--date'>
                                <CategorizedDropDown
                                    id='quickstart-search-date'
                                    placeholder={selectedDateOptionLabel}
                                    categories={dateCategories}
                                    disableSearch
                                    disableCategories
                                    noResultsText={t('quickstart.no_results')}
                                    onSelect={handleDateSelect}
                                />
                            </div>
                            <div className='quick-start-modal__date-time-item quick-start-modal__date-time-item--time'>
                                <CategorizedDropDown
                                    id='quickstart-search-time'
                                    placeholder={selectedTimeOptionLabel}
                                    categories={timeCategories}
                                    disableSearch
                                    disableCategories
                                    noResultsText={t('quickstart.no_results')}
                                    onSelect={handleTimeSelect}
                                />
                            </div>
                        </div>
                        {selectedDateOptionId === 'custom' && (
                            <div className='quick-start-modal__custom-date-range'>
                                <div className='quick-start-modal__custom-date-field'>
                                    <label htmlFor='quickstart-custom-from-date'>
                                        {t('quickstart.custom_date.from')}
                                    </label>
                                    <input
                                        id='quickstart-custom-from-date'
                                        type='date'
                                        value={customFromDate}
                                        min={today}
                                        max={customToDate || maxFutureDate}
                                        onChange={(event) => {
                                            handleCustomFromDateChange(event.target.value);
                                        }}
                                    />
                                </div>
                                <div className='quick-start-modal__custom-date-field'>
                                    <label htmlFor='quickstart-custom-to-date'>{t('quickstart.custom_date.to')}</label>
                                    <input
                                        id='quickstart-custom-to-date'
                                        type='date'
                                        value={customToDate}
                                        min={customFromDate > today ? customFromDate : today}
                                        max={maxFutureDate}
                                        onChange={(event) => {
                                            handleCustomToDateChange(event.target.value);
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        {showIntervalLimitedNotice && (
                            <div
                                className='quick-start-modal__interval-limited-notice'
                                role='status'
                                aria-live='polite'
                            >
                                {t('search_filters.interval_limited_notice')}
                            </div>
                        )}
                    </div>
                </div>
            </ModalPortal>
            {showPositionPermissionError && (
                <PositionPermissionError
                    onClose={() => {
                        setShowPositionPermissionError(false);
                    }}
                />
            )}
        </>
    );
}
